import api from "./api";

// User service using backend endpoints (excluding the last two with-relations)
// GET    /user                 -> [{ user_id, name, email, password?, role_id }]
// GET    /user/:id             -> { user_id, name, email, password?, role_id }
// POST   /user                 -> { user_id, name, email, role_id }
// PATCH  /user/:id             -> updated user
// DELETE /user/:id             -> { ok: true }

// Normalize API payloads to ensure consistent 'email' and 'name' keys
const normalizeUser = (u) => {
  if (!u || typeof u !== "object") return u;
  const email = u.email ?? u.emai ?? u.user_email ?? "";
  const name = u.name ?? u.username ?? u.full_name ?? u.user_name ?? "";
  return { ...u, email, name };
};

export async function getUsers() {
  const res = await api.get("/user");
  const arr =
    Array.isArray(res) ? res
    : Array.isArray(res?.data) ? res.data
    : [];
  return arr.map(normalizeUser);
}

export async function getUsersBasicInfo({ search = "" } = {}) {
  const query = new URLSearchParams();
  if (String(search || "").trim()) {
    query.append("search", String(search).trim());
  }

  const qs = query.toString();
  const res = await api.get(`/user/basic-info${qs ? `?${qs}` : ""}`);

  const data =
    Array.isArray(res?.data) ? res.data
    : Array.isArray(res) ? res
    : [];

  return data.map(normalizeUser);
}

export async function getUsersPaginated({
  page = 1,
  limit = 10,
  search = "",
  useCreatedBy = false,
} = {}) {
  const query = new URLSearchParams();
  if (page != null) query.append("page", String(page));
  if (limit != null) query.append("limit", String(limit));
  if (String(search || "").trim())
    query.append("search", String(search).trim());

  const endpointBase = useCreatedBy ? "/user/CreatedBy" : "/user";
  const qs = query.toString();
  const res = await api.get(`${endpointBase}${qs ? `?${qs}` : ""}`);

  const data =
    Array.isArray(res?.data) ? res.data
    : Array.isArray(res) ? res
    : [];
  const meta =
    res?.meta && typeof res.meta === "object" ?
      res.meta
    : {
        total: data.length,
        page: Number(page) || 1,
        limit: Number(limit) || 10,
        totalPages: 1,
      };

  return {
    data: data.map(normalizeUser),
    meta: {
      total: Number(meta.total) || 0,
      page: Number(meta.page) || 1,
      limit: Number(meta.limit) || Number(limit) || 10,
      totalPages: Number(meta.totalPages) || 1,
    },
  };
}

export async function createUser(payload) {
  // payload (from UI): { name, email, password, role_id, company_id }
  // Backend auto-increments user_id; do not send user_id in body
  // created_at is set to now on create
  const body = {
    name: payload.name,
    email: payload.email,
    password: payload.password,
    confirmPassword: payload.confirmPassword,
    role_id: payload.role_id,
    company_id: payload.company_id ?? null,
  };
  return api.post("/user", body);
}

export async function patchUser(userId, payload) {
  const idNum = Number(userId);
  if (!Number.isInteger(idNum)) throw new Error("userId must be an integer");
  const body = { ...payload };
  // Do NOT send the id in the body; backend expects it only in the URL
  delete body.user_id;
  delete body.id;
  return api.patch(`/user/${idNum}`, body);
}

export async function deleteUser(userId) {
  const idNum = Number(userId);
  if (!Number.isInteger(idNum)) throw new Error("userId must be an integer");
  return api.delete(`/user/${idNum}`);
}

// GET /hierarchical-view/relation/CreatedBy/:userId
// Response shape: { message: string, users: [ { user fields... } ] }
export async function getUsersCreatedBy(userId) {
  if (userId === undefined || userId === null || userId === "")
    throw new Error("userId is required");
  const idNum = Number(userId);
  if (!Number.isInteger(idNum)) throw new Error("userId must be an integer");
  const res = await api.get(`/hierarchical-view/relation/CreatedBy/${idNum}`);
  const list =
    Array.isArray(res) ? res
    : Array.isArray(res?.users) ? res.users
    : [];
  return list.map(normalizeUser);
}

// Reset password for a user
export async function resetUserPassword(userId, payload) {
  const idNum = Number(userId);
  if (!Number.isInteger(idNum)) throw new Error("userId must be an integer");
  const body = {
    password: payload.password,
    confirmPassword: payload.confirmPassword,
  };
  return api.patch(`/user/${idNum}`, body);
}

// GET /hierarchical-view/createAdminUser/:userId
// Returns nested tree: { userId, name, createdUsers: [ { user_id|userId, name, createdUsers: [...] } ] }
export async function getAdminCreatedTree(userId) {
  if (userId === undefined || userId === null || userId === "")
    throw new Error("userId is required");
  const idNum = Number(userId);
  if (!Number.isInteger(idNum)) throw new Error("userId must be an integer");
  const res = await api.get(`/hierarchical-view/createAdminUser/${idNum}`);
  return res || null;
}

// GET /user/notification/:userId
// Returns array of { id, user_id, message, is_read, created_at, updated_at }
export async function getNotifications(userId) {
  if (userId === undefined || userId === null || userId === "")
    throw new Error("userId is required");
  const idNum = Number(userId);
  if (!Number.isInteger(idNum)) throw new Error("userId must be an integer");
  const res = await api.get(`/user/notification/${idNum}`);
  return Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
}

// PATCH /user/notification/:notificationId/read  (mark single notification read)
export async function markNotificationRead(notificationId) {
  const idNum = Number(notificationId);
  if (!Number.isInteger(idNum)) throw new Error("notificationId must be an integer");
  return api.patch(`/user/notification/${idNum}/read`, {});
}

// GET /user/processes/pending-approval
export async function getMyPendingApprovals() {
  return api.request("/user/processes/pending-approval", {
    method: "GET",
    suppressErrorPage: true,
  });
}

// DELETE /user/permissionProcess/:permissionId
export async function deletePendingApproval(permissionId) {
  const idNum = Number(permissionId);
  if (!Number.isInteger(idNum)) throw new Error("permissionId must be an integer");
  return api.request(`/user/permissionProcess/${idNum}`, {
    method: "DELETE",
    suppressErrorPage: true,
  });
}
